import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MatchScoringModal from './MatchScoringModal';
import LeaderboardBowlerDetail from './LeaderboardBowlerDetail';
import LeaderboardTeamDetail from './LeaderboardTeamDetail';

const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// ” SE Types ”
interface BracketMatchupData {
  matchup_number: number;
  entry1_id: number | null; entry1_name: string | null; entry1_score: number | null;
  entry2_id: number | null; entry2_name: string | null; entry2_score: number | null;
  winner_entry_id: number | null; is_bye: boolean;
}
interface BracketRoundData { round_number: number; game_number: number | null; matchups: BracketMatchupData[]; }
interface BracketData { bracket_number: number; rounds: BracketRoundData[]; }
interface SEResult {
  id: number; name: string; type: string;
  applies_to: string; applies_to_game_numbers: string | null;
  hdcp?: boolean;
  standings?: any[];
  cashers?: any[];
  computed_rounds?: any[];
  mystery_target?: number | null;
  matching_bowlers?: any[];
  payouts?: { bowler_name: string; amount: number; notes: string | null }[];
  clean_game_winners?: string;
  pot_results?: any[];
  config?: any[];
  brackets?: BracketData[];
  totalRounds?: number;
  cost?: number;
}

const TYPE_LABELS: Record<string, string> = {
  high_game: 'High Game', high_series: 'High Series',
  mystery_score: 'Mystery Score', mystery_series: 'Mystery Series',
  side_pots: 'Side Pots', survivor: 'Survivor',
  eliminator: 'Eliminator', clean_game: 'Clean Game',
  bracket: 'Bracket', custom: 'Custom',
};

// ” Print Brackets ”
function printBrackets(seName: string, brackets: any[], totalRounds: number, cost: number) {
  const buildMu = (m: any): string => {
    if (!m) return '<table class="mu"><tr><td class="mu-name"><em class="tbd">TBD</em></td><td class="mu-score"></td></tr><tr><td class="mu-name"><em class="tbd">TBD</em></td><td class="mu-score"></td></tr></table>';
    const e1Won = m.winner_entry_id === m.entry1_id;
    const e2Won = m.winner_entry_id === m.entry2_id;
    const r1 = `<tr style="background:${e1Won?'#d1fae5':'white'}"><td class="mu-name" style="font-weight:${e1Won?700:'normal'}">${m.entry1_name||'<em class="tbd">TBD</em>'}</td><td class="mu-score" style="font-weight:${e1Won?700:'normal'}">${m.entry1_score!=null?m.entry1_score:''}</td></tr>`;
    const r2 = `<tr style="background:${e2Won?'#d1fae5':'white'}"><td class="mu-name" style="font-weight:${e2Won?700:'normal'}">${m.is_bye?'<em class="tbd">BYE</em>':(m.entry2_name||'<em class="tbd">TBD</em>')}</td><td class="mu-score" style="font-weight:${e2Won?700:'normal'}">${!m.is_bye&&m.entry2_score!=null?m.entry2_score:''}</td></tr>`;
    return `<table class="mu"><tbody>${r1}${r2}</tbody></table>`;
  };
  const bracketHtml = brackets.map(b => {
    const cols = Array.from({length:totalRounds},(_,ri)=>{
      const rn=ri+1; const round=b.rounds.find((r:any)=>r.round_number===rn);
      const slotCount=Math.pow(2,totalRounds-rn); const slotH=Math.pow(2,rn-1)*40;
      const hdr=rn===totalRounds?'Final':`R${rn}${round?.game_number!=null?` G${round.game_number}`:''}`;
      const cells=Array.from({length:slotCount},(_,mi)=>`<div class="slot" style="height:${slotH}px">${buildMu(round?.matchups?.[mi])}</div>`).join('');
      return `<div class="col"><div class="col-hdr">${hdr}</div>${cells}</div>`;
    }).join('');
    return `<div class="bracket"><div class="b-title">Bracket #${b.bracket_number}</div><div class="b-rounds">${cols}</div></div>`;
  }).join('');
  const html=`<!DOCTYPE html><html><head><title>Brackets “ ${seName}</title><style>@page{size:A4 portrait;margin:12mm}body{font-family:Arial,sans-serif;font-size:10px;color:#111;margin:0}h2{font-size:14px;margin:0 0 2px}.sub{color:#6b7280;font-size:10px;margin:0 0 10px}.page-columns{columns:2;column-gap:6mm}.bracket{break-inside:avoid;border:1px solid #d1d5db;border-radius:4px;padding:5px;margin-bottom:8px}.b-title{font-weight:700;font-size:10px;margin-bottom:4px}.b-rounds{display:flex;gap:4px}.col{flex:0 0 auto}.col-hdr{text-align:center;font-size:8px;color:#6b7280;border-bottom:1px solid #e5e7eb;padding-bottom:2px;margin-bottom:2px}.slot{display:flex;align-items:center}table.mu{width:82px;border-collapse:collapse;border:1px solid #9ca3af}table.mu td{border-bottom:1px solid #d1d5db;padding:1px 3px}table.mu tr:last-child td{border-bottom:none}.mu-name{max-width:62px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:7.5px}.mu-score{width:18px;text-align:right}.tbd{color:#9ca3af;font-size:8px}.footer{color:#9ca3af;font-size:9px;margin-top:10px}</style></head><body><h2>Brackets “ ${seName}</h2><p class="sub">Entry: $${Number(cost).toFixed(2)} ¢ ${brackets.length} bracket${brackets.length!==1?'s':''} ¢ ${totalRounds} rounds</p><div class="page-columns">${bracketHtml}</div><p class="footer">Printed ${new Date().toLocaleString()}</p></body></html>`;
  const w=window.open('','_blank','width=820,height=1060');
  if(w){w.document.write(html);w.document.close();setTimeout(()=>w.print(),400);}
}

// ” Bracket Tree Visual 
const BracketTreeView: React.FC<{bracket:BracketData;totalRounds:number}> = ({bracket,totalRounds}) => (
  <div className="overflow-x-auto pb-2">
    <div className="flex gap-2" style={{minWidth:`${totalRounds*188}px`}}>
      {Array.from({length:totalRounds},(_,ri)=>{
        const roundNum=ri+1;
        const round=bracket.rounds.find(r=>r.round_number===roundNum);
        const slotCount=Math.pow(2,totalRounds-roundNum);
        const slotHeightPx=Math.pow(2,roundNum-1)*58;
        const header=roundNum===totalRounds?'Final':`Round ${roundNum}${round?.game_number!=null?` · G${round.game_number}`:''}`;
        return (
          <div key={roundNum} className="flex flex-col" style={{flex:'0 0 180px'}}>
            <div className="text-[10px] text-gray-500 text-center pb-1 border-b border-gray-700 mb-1">{header}</div>
            <div className="flex flex-col flex-1">
              {Array.from({length:slotCount},(_,mi)=>{
                const m=round?.matchups?.[mi];
                const e1Won=m&&m.winner_entry_id===m.entry1_id;
                const e2Won=m&&m.winner_entry_id===m.entry2_id;
                return (
                  <div key={mi} className="flex items-center justify-center" style={{height:`${slotHeightPx}px`}}>
                    <div className={`w-full border rounded overflow-hidden text-[11px] ${m?'border-gray-600':'border-gray-800 opacity-40'}`}>
                      <div className={`flex items-center justify-between px-2 py-1 border-b border-gray-700 ${e1Won?'bg-green-900/40 text-green-300 font-semibold':m?.winner_entry_id?'text-gray-500':'text-gray-300'}`}>
                        <span className="truncate max-w-[110px]">{m?.entry1_name??<span className="text-gray-600 italic text-[10px]">TBD</span>}</span>
                        <div className="flex items-center gap-1 flex-shrink-0 ml-1">
                          {m?.entry1_score!=null&&<span className="font-mono text-[10px]">{m.entry1_score}</span>}
                          {e1Won&&<svg className="w-3 h-3 text-green-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>}
                        </div>
                      </div>
                      <div className={`flex items-center justify-between px-2 py-1 ${e2Won?'bg-green-900/40 text-green-300 font-semibold':m?.winner_entry_id?'text-gray-500':'text-gray-300'}`}>
                        <span className="truncate max-w-[110px]">{m?.is_bye?<span className="text-gray-600 italic text-[10px]">BYE</span>:(m?.entry2_name??<span className="text-gray-600 italic text-[10px]">TBD</span>)}</span>
                        <div className="flex items-center gap-1 flex-shrink-0 ml-1">
                          {!m?.is_bye&&m?.entry2_score!=null&&<span className="font-mono text-[10px]">{m.entry2_score}</span>}
                          {e2Won&&<svg className="w-3 h-3 text-green-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

// ” SE Result Card ”
const SEResultCard: React.FC<{se:SEResult;myBowlerId:number|null;isEnrolled:boolean}> = ({se,myBowlerId,isEnrolled}) => {
  const typeLabel = TYPE_LABELS[se.type] || se.type;
  const MeBadge = () => <span className="ml-1.5 px-1.5 py-0.5 bg-orange-500/30 text-orange-200 text-[9px] rounded-full font-bold border border-orange-400/40">You</span>;
  return (
    <div className={`bg-gray-800/60 rounded-xl border ${isEnrolled?'border-orange-400/40':'border-gray-700/60'} overflow-hidden`}>
      <div className="px-4 py-3 bg-gray-700/40 flex items-center justify-between border-b border-gray-700/60">
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-white text-sm">{se.name}</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-600/60 text-gray-300 border border-gray-500/40">{typeLabel}</span>
            {se.hdcp&&<span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-teal-600/20 text-teal-400 border border-teal-600/30">HDCP</span>}
            {isEnrolled&&<span className="text-[10px] px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-300 border border-orange-400/30 font-semibold">You're in this</span>}
          </div>
          <button onClick={()=>window.location.reload()} className="flex items-center gap-1 text-[9px] text-gray-500 hover:text-gray-300 transition-colors w-fit">
            <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
            Refresh
          </button>
        </div>
        <div className="flex items-center gap-2">
          {se.applies_to&&se.applies_to!=='individual'&&<span className="text-[10px] text-gray-400">{se.applies_to}</span>}
          {se.type==='bracket'&&se.brackets&&se.brackets.length>0&&(
            <button onClick={()=>printBrackets(se.name,se.brackets!,se.totalRounds??3,se.cost??0)}
              className="flex items-center gap-1 px-2 py-1 bg-indigo-700/50 hover:bg-indigo-700 text-white text-[10px] rounded-md font-medium transition-colors">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2z"/></svg>
              Print
            </button>
          )}
        </div>
      </div>
      <div className="px-4 py-3">
        {/* SURVIVOR */}
        {se.type==='survivor'&&(()=>{
          const rounds=se.computed_rounds||[];
          if(rounds.length===0) return <p className="text-xs text-gray-500 py-2">No rounds configured yet.</p>;
          return (<div className="space-y-2">
            {rounds.map((r:any)=>(
              <div key={r.game_number} className="bg-gray-700/30 rounded-lg p-3 border border-gray-600/50">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-semibold text-white">Game {r.game_number}</span>
                  <div className="flex items-center gap-2 text-[10px]">
                    <span className="text-gray-400">Min: <strong className="text-white">{r.survivor_score}</strong></span>
                    {r.round_payout>0&&<span className="text-green-400 bg-green-900/30 px-1.5 py-0.5 rounded">${r.round_payout}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-3 text-xs mb-2">
                  <span className="text-emerald-400">{r.survivors_count??0} survived</span>
                  {(r.eliminated_count??0)>0&&<span className="text-red-400">{r.eliminated_count} out</span>}
                  {r.pending_list?.length>0&&<span className="text-gray-500">{r.pending_list.length} pending</span>}
                </div>
                {r.survivor_list?.length>0&&<div className="flex flex-wrap gap-1 mb-1">{r.survivor_list.map((b:any)=><span key={b.bowler_id} className={`text-[10px] px-1.5 py-0.5 rounded ${b.bowler_id===myBowlerId?'bg-orange-800/50 text-orange-200 border border-orange-400/40':'bg-emerald-900/40 text-emerald-300'}`}>{b.bowler_name.split(' ')[0]} ({b.score}){b.bowler_id===myBowlerId?' *':''}</span>)}</div>}
                {r.eliminated_list?.length>0&&<div className="flex flex-wrap gap-1">{r.eliminated_list.map((b:any)=><span key={b.bowler_id} className={`text-[10px] px-1.5 py-0.5 rounded line-through ${b.bowler_id===myBowlerId?'text-orange-400/70 bg-red-900/30 border border-red-700/40':'bg-red-900/30 text-red-400/80'}`}>{b.bowler_name.split(' ')[0]} ({b.score}){b.bowler_id===myBowlerId?' *':''}</span>)}</div>}
              </div>
            ))}
            {se.cashers&&se.cashers.length>0&&<div className="p-3 bg-yellow-900/20 border border-yellow-500/30 rounded-lg"><div className="text-xs font-bold text-yellow-300 mb-1.5">Cashers</div>{se.cashers.map((c:any,i:number)=><div key={c.bowler_id} className="flex items-center gap-2 text-xs py-0.5"><span className="text-gray-400">{i+1}.</span><span className={c.bowler_id===myBowlerId?'text-orange-200 font-semibold':'text-white'}>{c.bowler_name}</span>{c.bowler_id===myBowlerId&&<MeBadge/>}{c.payout>0&&<span className="text-green-400 text-[10px]">+${c.payout}</span>}</div>)}</div>}
          </div>);
        })()}
        {/* ELIMINATOR */}
        {se.type==='eliminator'&&(()=>{
          const rounds=se.computed_rounds||[];
          if(rounds.length===0) return <p className="text-xs text-gray-500 py-2">No rounds configured yet.</p>;
          return (<div className="space-y-2">
            {rounds.map((r:any)=>(
              <div key={r.game_number} className="bg-gray-700/30 rounded-lg p-3 border border-gray-600/50">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-semibold text-white">Game {r.game_number}</span>
                  <div className="flex items-center gap-2 text-[10px]">
                    <span className="text-gray-400">Advance: <strong className="text-white">{r.advancement_percentage}%</strong></span>
                    {r.cutoff_score!=null&&<span className="text-gray-400">cutoff: {r.cutoff_score}</span>}
                    {r.round_payout>0&&<span className="text-green-400 bg-green-900/30 px-1.5 py-0.5 rounded">${r.round_payout}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-3 text-xs mb-2">
                  <span className="text-orange-400">{r.advancers_count??0} advancing</span>
                  {(r.eliminated_count??0)>0&&<span className="text-red-400">{r.eliminated_count} out</span>}
                  {r.pending_list?.length>0&&<span className="text-gray-500">{r.pending_list.length} pending</span>}
                </div>
                {r.advancer_list?.length>0&&<div className="flex flex-wrap gap-1 mb-1">{r.advancer_list.map((b:any)=><span key={b.bowler_id} className={`text-[10px] px-1.5 py-0.5 rounded ${b.bowler_id===myBowlerId?'bg-orange-800/50 text-orange-200 border border-orange-400/40':'bg-orange-900/40 text-orange-300'}`}>{b.bowler_name.split(' ')[0]} ({b.score}){b.bowler_id===myBowlerId?' *':''}</span>)}</div>}
                {r.eliminated_list?.length>0&&<div className="flex flex-wrap gap-1">{r.eliminated_list.map((b:any)=><span key={b.bowler_id} className={`text-[10px] px-1.5 py-0.5 rounded line-through ${b.bowler_id===myBowlerId?'text-orange-400/70 bg-red-900/30 border border-red-700/40':'bg-red-900/30 text-red-400/80'}`}>{b.bowler_name.split(' ')[0]} ({b.score}){b.bowler_id===myBowlerId?' *':''}</span>)}</div>}
              </div>
            ))}
            {se.cashers&&se.cashers.length>0&&<div className="p-3 bg-yellow-900/20 border border-yellow-500/30 rounded-lg"><div className="text-xs font-bold text-yellow-300 mb-1.5">ðŸ† Finalists</div>{se.cashers.map((c:any,i:number)=><div key={c.bowler_id} className="flex items-center gap-2 text-xs py-0.5"><span className="text-gray-400">{i+1}.</span><span className={c.bowler_id===myBowlerId?'text-orange-200 font-semibold':'text-white'}>{c.bowler_name}</span>{c.bowler_id===myBowlerId&&<MeBadge/>}{c.payout>0&&<span className="text-green-400 text-[10px]">+${c.payout}</span>}</div>)}</div>}
          </div>);
        })()}
        {/* MYSTERY SCORE / SERIES */}
        {(se.type==='mystery_score'||se.type==='mystery_series')&&(()=>{
          const isSeries=se.type==='mystery_series'; const target=se.mystery_target??null;
          const matches:any[]=se.matching_bowlers||[]; const payouts=se.payouts||[];
          return (<div>
            {target!=null?<p className="text-xs text-blue-400 mb-3">Target: <span className="font-bold text-white text-sm">{target}</span>{isSeries?' (series)':''}</p>:<p className="text-xs text-gray-500 mb-3">Target not set yet.</p>}
            {payouts.length>0&&<div className="mb-4"><p className="text-xs text-yellow-400 font-semibold mb-1.5">Paid Winners</p><div className="space-y-1.5">{payouts.map((p,i)=><div key={i} className="flex items-center justify-between rounded-lg px-3 py-2 bg-yellow-900/30 border border-yellow-500/40"><span className="text-sm font-medium text-white">{p.bowler_name}</span><span className="text-yellow-300 text-xs font-bold">${Number(p.amount).toFixed(2)}</span></div>)}</div></div>}
            {matches.length===0?<div className="text-xs text-gray-500 text-center py-3">{target!=null?'No matches yet':'”'}</div>:<div className="space-y-1.5"><p className="text-xs text-green-400 font-semibold">{matches.length} match{matches.length>1?'es':''}!</p>{matches.map((b:any)=><div key={b.bowler_id} className={`flex items-center justify-between rounded-lg px-3 py-2 border ${b.bowler_id===myBowlerId?'bg-orange-900/30 border-orange-500/40':'bg-green-900/30 border-green-500/40'}`}><div><div className="text-sm font-medium text-white flex items-center gap-1">{b.bowler_name}{b.bowler_id===myBowlerId&&<MeBadge/>}</div><div className="text-green-400 text-xs font-bold">{isSeries?`${b.total_score} series `:(b.matching_games||[]).map((g:any)=>`G${g.game}: ${g.score}`).join(', ')}</div></div></div>)}</div>}
          </div>);
        })()}
        {/* SIDE POTS */}
        {se.type==='side_pots'&&(()=>{
          const pots=se.pot_results||[];
          if(pots.length===0) return <p className="text-xs text-gray-500 py-2">No side pot results yet.</p>;
          return (<div className="space-y-3">{pots.map((pot:any,pi:number)=>{
            const label=pot.config_type==='game_series'?`Series (G${pot.game_identifier})`:`Game ${pot.game_identifier}`;
            const all:any[]=pot.all_entries||pot.winners||[];
            const cutoff=all[pot.winner_count-1]?.score??null;
            return (<div key={pi} className="bg-gray-700/30 rounded-lg border border-gray-600/50 overflow-hidden">
              <div className="px-3 py-2 bg-gray-700/50 border-b border-gray-600/50 flex items-center justify-between"><span className="text-xs font-semibold text-white">{label}</span><span className="text-[10px] text-gray-400">Top {pot.winners_pct}% †’ {pot.winner_count} winner{pot.winner_count!==1?'s':''}</span></div>
              <div className="max-h-40 overflow-y-auto">{all.length===0?<div className="px-3 py-2 text-xs text-gray-500 text-center">No scores yet</div>:all.map((b:any)=>{
                const isWinner=b.rank<=pot.winner_count; const isTied=!isWinner&&cutoff!==null&&b.score===cutoff; const isMe=b.bowler_id===myBowlerId;
                return <div key={b.bowler_id} className={`flex items-center justify-between px-3 py-1.5 text-xs border-b border-gray-700/50 last:border-0 ${isMe?'bg-orange-900/20':isWinner?'bg-yellow-900/20 text-yellow-200':isTied?'bg-orange-900/20 text-orange-200':'text-gray-400'}`}><div className="flex items-center gap-1.5"><span>{b.rank}. {b.bowler_name}</span>{isMe&&<MeBadge/>}{isTied&&!isMe&&<span className="text-[9px] bg-orange-700/60 text-orange-200 px-1.5 py-0.5 rounded-full">TIE</span>}</div><span className={`font-bold ${isWinner?'text-yellow-300':isTied?'text-orange-300':''}`}>{b.score}</span></div>;
              })}</div>
            </div>);
          })}</div>);
        })()}
        {/* HIGH GAME / HIGH SERIES */}
        {(se.type==='high_game'||se.type==='high_series')&&(()=>{
          const isHigh=se.type==='high_series'; const standings=se.standings||[];
          const winnerScore=standings.length>0?(isHigh?standings[0].total_score:standings[0].best_game):null;
          if(standings.length===0) return <p className="text-xs text-gray-500 py-2">No scores entered yet.</p>;
          return (<div className="bg-gray-700/30 rounded-lg border border-gray-600/50 overflow-hidden">{standings.map((s:any,i:number)=>{
            const score=isHigh?s.total_score:s.best_game; const isWinner=i===0;
            const isTied=!isWinner&&winnerScore!==null&&score===winnerScore; const isMe=s.bowler_id===myBowlerId;
            return <div key={s.bowler_id} className={`flex items-center justify-between px-3 py-1.5 text-xs border-b border-gray-700/50 last:border-0 ${isMe?'bg-orange-900/20':isWinner?'bg-yellow-900/20 text-yellow-200':isTied?'bg-orange-900/20 text-orange-200':'text-gray-400'}`}><div className="flex items-center gap-1.5"><span>{i+1}. {s.bowler_name}</span>{isMe&&<MeBadge/>}{isTied&&!isMe&&<span className="text-[9px] bg-orange-700/60 text-orange-200 px-1.5 py-0.5 rounded-full">TIE</span>}</div><span className={`font-bold ${isWinner?'text-yellow-300':isTied?'text-orange-300':''}`}>{score??'”'}{isHigh?' ser':''}</span></div>;
          })}</div>);
        })()}
        {/* BRACKET */}
        {se.type==='bracket'&&(()=>{
          const brackets=se.brackets||[]; const totalRounds=se.totalRounds||3;
          if(brackets.length===0) return <p className="text-xs text-gray-500 py-2 italic">No brackets set up yet.</p>;
          return (<div className="max-h-[520px] overflow-y-auto space-y-4 pr-1">{brackets.map(bracket=>(
            <div key={bracket.bracket_number} className="bg-gray-900/40 rounded-lg border border-gray-700/60 overflow-hidden">
              <div className="px-3 py-1.5 bg-gray-700/40 border-b border-gray-700/60"><span className="text-[11px] font-semibold text-indigo-300">Bracket #{bracket.bracket_number}</span></div>
              <div className="p-2"><BracketTreeView bracket={bracket} totalRounds={totalRounds}/></div>
            </div>
          ))}</div>);
        })()}
        {/* CLEAN GAME */}
        {se.type==='clean_game'&&<div className="text-xs text-gray-300 py-2"><span className="font-semibold text-white">Clean Game Winners: </span>{se.clean_game_winners||'Not yet determined'}</div>}
        {/* CUSTOM */}
        {se.type==='custom'&&(()=>{
          const standings=se.standings||[];
          if(standings.length===0) return <p className="text-xs text-gray-500 py-2">No scores yet.</p>;
          return (<div className="bg-gray-700/30 rounded-lg border border-gray-600/50 overflow-hidden">{standings.map((s:any,i:number)=>{
            const isMe=s.bowler_id===myBowlerId;
            return <div key={s.bowler_id} className={`flex items-center justify-between px-3 py-1.5 text-xs border-b border-gray-700/50 last:border-0 ${isMe?'bg-orange-900/20':i===0?'bg-yellow-900/20 text-yellow-200':'text-gray-400'}`}><div className="flex items-center gap-1.5"><span>{i+1}. {s.bowler_name}</span>{isMe&&<MeBadge/>}</div><span className={`font-bold ${i===0?'text-yellow-300':''}`}>{s.best_game??s.total_score??'”'}</span></div>;
          })}</div>);
        })()}
      </div>
    </div>
  );
};

// ” Leaderboard types 
type MostPointsEntry       = { team_id: number; team_name: string; points: number };
type OverallPinfallEntry   = { bowler_id: number; bowler_name: string; team_id: number; team_name: string; total_pinfall: number; games_played: number; average: number };
type OverallTeamPinfallEntry = { team_id: number; team_name: string; total_pinfall: number; games_played: number; average: number };

// ” Leaderboard Card ”
const LeaderboardCard: React.FC<{ leagueId: string }> = ({ leagueId }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [mostPoints, setMostPoints] = useState<MostPointsEntry[]>([]);
  const [overallPinfall, setOverallPinfall] = useState<OverallPinfallEntry[]>([]);
  const [overallTeamPinfall, setOverallTeamPinfall] = useState<OverallTeamPinfallEntry[]>([]);
  const [divisionPinfallData, setDivisionPinfallData] = useState<Record<string, OverallPinfallEntry[]>>({});
  const [divisionLeaderboards, setDivisionLeaderboards] = useState<string[]>([]);
  const [selectedBowler, setSelectedBowler] = useState<{ id: number; name: string; teamName: string } | null>(null);
  const [selectedTeam, setSelectedTeam] = useState<{ id: number; name: string } | null>(null);
  const token = localStorage.getItem('token') || '';

  useEffect(() => {
    setLoading(true);
    const h = { Authorization: `Bearer ${token}` };
    fetch(`${BACKEND_URL}/player/leaderboard/leagues/${leagueId}/payout-settings`, { headers: h })
      .then(r => r.json())
      .then(async (payoutData) => {
        const divSettings = (payoutData as any[]).filter(s => s.name === 'Overall Pinfall' && s.description?.startsWith('Division '));
        const divDescs: string[] = divSettings.map((s: any) => s.description);
        setDivisionLeaderboards(divDescs);
        const [pts, pf, tpf, ...divResults] = await Promise.all([
          fetch(`${BACKEND_URL}/player/leaderboard/leagues/${leagueId}/most-points`, { headers: h }).then(r => r.json()),
          fetch(`${BACKEND_URL}/player/leaderboard/leagues/${leagueId}/overall-pinfall`, { headers: h }).then(r => r.json()),
          fetch(`${BACKEND_URL}/player/leaderboard/leagues/${leagueId}/overall-team-pinfall`, { headers: h }).then(r => r.json()),
          ...divSettings.map((dl: any) => {
            const div = dl.description.replace('Division ', '').toLowerCase();
            return fetch(`${BACKEND_URL}/player/leaderboard/leagues/${leagueId}/overall-pinfall?division=${div}`, { headers: h }).then(r => r.json());
          }),
        ]);
        setMostPoints(pts); setOverallPinfall(pf); setOverallTeamPinfall(tpf);
        const divData: Record<string, OverallPinfallEntry[]> = {};
        divDescs.forEach((desc, i) => { divData[desc] = divResults[i]; });
        setDivisionPinfallData(divData);
        if ((payoutData as any[]).length > 0) setActiveTab(`${payoutData[0].name}|||${payoutData[0].description}`);
        else setActiveTab('Most Points|||points');
      })
      .catch(() => {
        const h2 = { Authorization: `Bearer ${token}` };
        Promise.all([
          fetch(`${BACKEND_URL}/player/leaderboard/leagues/${leagueId}/most-points`, { headers: h2 }).then(r => r.json()),
          fetch(`${BACKEND_URL}/player/leaderboard/leagues/${leagueId}/overall-pinfall`, { headers: h2 }).then(r => r.json()),
          fetch(`${BACKEND_URL}/player/leaderboard/leagues/${leagueId}/overall-team-pinfall`, { headers: h2 }).then(r => r.json()),
        ]).then(([pts, pf, tpf]) => { setMostPoints(pts); setOverallPinfall(pf); setOverallTeamPinfall(tpf); setActiveTab('Most Points|||points'); });
      })
      .finally(() => setLoading(false));
  }, [leagueId, token]);

  const tabs = (() => {
    const result: Array<{ key: string; name: string; description: string }> = [];
    const seen = new Set<string>();
    ([
      { name: 'Most Points', description: 'points' },
      { name: 'Overall Pinfall', description: 'Overall' },
      { name: 'Overall Team Pinfall', description: 'team-pinfall' },
    ] as const).forEach(({ name, description }) => {
      const key = `${name}|||${description}`;
      if (!seen.has(key)) { result.push({ key, name, description }); seen.add(key); }
    });
    divisionLeaderboards.forEach(dd => {
      const key = `Overall Pinfall|||${dd}`;
      if (!seen.has(key)) { result.push({ key, name: 'Overall Pinfall', description: dd }); seen.add(key); }
    });
    return result;
  })();

  const [tabName, tabDesc] = activeTab.split('|||');

  return (
    <div className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-bold text-white">{t('player.leaderboard') || 'Leaderboard'}</h2>
        <button onClick={() => window.location.reload()} className="flex items-center gap-1 text-[9px] text-blue-300/60 hover:text-white transition-colors">
          <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
          Refresh
        </button>
      </div>
      <div className="flex flex-wrap gap-1 mb-3">
        {tabs.map(({ key, name, description }) => (
          <button key={key} onClick={() => setActiveTab(key)}
            className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition ${
              activeTab === key
                ? name === 'Most Points' ? 'bg-blue-600 text-white' : name === 'Overall Team Pinfall' ? 'bg-orange-600 text-white' : 'bg-purple-600 text-white'
                : 'bg-white/10 text-blue-200 hover:bg-blue-700/30'
            }`}>
            {description.startsWith('Division ') ? description : name === 'Most Points' ? t('leaderboard.points') : name === 'Overall Pinfall' ? t('player.bowlerName') : t('league.team')}
          </button>
        ))}
      </div>
      <div className="bg-white/5 rounded-xl p-2 min-h-[240px] max-h-[380px] overflow-y-auto" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.2) transparent' }}>
        {loading ? <div className="text-blue-200 text-center py-8 text-sm">Loading...</div> : (
          tabName === 'Most Points' ? (
            <table className="w-full text-xs">
              <thead><tr className="text-blue-200 border-b border-white/10">
                <th className="py-1.5 text-left">{t('player.rank')}</th>
                <th className="py-1.5 text-left">{t('league.team')}</th>
                <th className="py-1.5 text-right">{t('leaderboard.points')}</th>
              </tr></thead>
              <tbody>
                {mostPoints.length === 0 && <tr><td colSpan={3} className="text-blue-300 text-center py-6 text-xs">{t('player.noDataYet')}</td></tr>}
                {mostPoints.map((entry, i) => (
                  <tr key={entry.team_id} onClick={() => setSelectedTeam({ id: entry.team_id, name: entry.team_name })}
                    className="border-b border-white/5 hover:bg-blue-900/30 cursor-pointer">
                    <td className="py-1.5 text-white">{i + 1}</td>
                    <td className="py-1.5 text-white">{entry.team_name}</td>
                    <td className="py-1.5 text-green-300 text-right font-bold">{entry.points}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : tabName === 'Overall Team Pinfall' ? (
            <table className="w-full text-xs">
              <thead><tr className="text-orange-200 border-b border-white/10">
                <th className="py-1.5 text-left">{t('player.rank')}</th>
                <th className="py-1.5 text-left">{t('league.team')}</th>
                <th className="py-1.5 text-right">{t('leaderboard.avg')}</th>
                <th className="py-1.5 text-right">{t('leaderboard.pinfall')}</th>
              </tr></thead>
              <tbody>
                {overallTeamPinfall.length === 0 && <tr><td colSpan={4} className="text-blue-300 text-center py-6 text-xs">{t('player.noDataYet')}</td></tr>}
                {overallTeamPinfall.map((entry, i) => (
                  <tr key={entry.team_id} className="border-b border-white/5 hover:bg-orange-900/30 cursor-pointer">
                    <td className="py-1.5 text-white">{i + 1}</td>
                    <td className="py-1.5 text-white">{entry.team_name}</td>
                    <td className="py-1.5 text-green-300 text-right">{entry.average}</td>
                    <td className="py-1.5 text-white text-right">{entry.total_pinfall}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            (() => {
              const data = tabDesc?.startsWith('Division ') ? (divisionPinfallData[tabDesc] || []) : overallPinfall;
              return (
                <table className="w-full text-xs">
                  <thead><tr className="text-purple-200 border-b border-white/10">
                    <th className="py-1.5 text-left">{t('player.rank')}</th>
                    <th className="py-1.5 text-left">{t('player.bowlerName')}</th>
                    <th className="py-1.5 text-left">{t('league.team')}</th>
                    <th className="py-1.5 text-right">{t('leaderboard.avg')}</th>
                    <th className="py-1.5 text-right">{t('leaderboard.pinfall')}</th>
                  </tr></thead>
                  <tbody>
                    {data.length === 0 && <tr><td colSpan={5} className="text-blue-300 text-center py-6 text-xs">{t('player.noDataYet')}</td></tr>}
                    {data.map((entry, i) => (
                      <tr key={entry.bowler_id} onClick={() => setSelectedBowler({ id: entry.bowler_id, name: entry.bowler_name, teamName: entry.team_name })}
                        className="border-b border-white/5 hover:bg-purple-900/30 cursor-pointer">
                        <td className="py-1.5 text-white">{i + 1}</td>
                        <td className="py-1.5 text-white">{entry.bowler_name}</td>
                        <td className="py-1.5 text-blue-200">{entry.team_name}</td>
                        <td className="py-1.5 text-green-300 text-right">{entry.average}</td>
                        <td className="py-1.5 text-white text-right">{entry.total_pinfall}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              );
            })()
          )
        )}
      </div>
      {selectedBowler && (
        <LeaderboardBowlerDetail bowlerId={selectedBowler.id} bowlerName={selectedBowler.name} teamName={selectedBowler.teamName} leagueId={Number(leagueId)} onClose={() => setSelectedBowler(null)} />
      )}
      {selectedTeam && (
        <LeaderboardTeamDetail teamId={selectedTeam.id} teamName={selectedTeam.name} leagueId={Number(leagueId)} onClose={() => setSelectedTeam(null)} />
      )}
    </div>
  );
};

// ” Main Component ”
const BowlerLeagueView: React.FC = () => {
  const { leagueId } = useParams<{ leagueId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [scheduleData, setScheduleData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isHidden, setIsHidden] = useState(false);
  const [hiddenLeagueName, setHiddenLeagueName] = useState('');
  const [leagueRules, setLeagueRules] = useState('');
  const [showRules, setShowRules] = useState(false);
  const [myTeam, setMyTeam] = useState<{ id: number; name: string; points: number } | null>(null);
  const [myBowlerId, setMyBowlerId] = useState<number | null>(null);
  const [selectedSquad, setSelectedSquad] = useState<any>(null);
  const [viewMode, setViewMode] = useState<'league' | 'side_action'>('league');
  const [saResults, setSAResults] = useState<SEResult[]>([]);
  const [loadingSA, setLoadingSA] = useState(false);
  const [saError, setSAError] = useState('');
  const saCache = useRef<Record<number, SEResult[]>>({});
  const [selectedMatchId, setSelectedMatchId] = useState<number | null>(null);

  const token = localStorage.getItem('token') || '';
  const authHeader = { Authorization: `Bearer ${token}` };

  //  Fetch schedule 
  useEffect(() => {
    if (!leagueId) return;
    setLoading(true);
    fetch(`${BACKEND_URL}/player/schedule/${leagueId}`, { headers: authHeader })
      .then(r => r.json())
      .then(data => {
        if (data.hidden) { setIsHidden(true); setHiddenLeagueName(data.league?.name || ''); return; }
        setScheduleData(data);
        if (data.squads?.length > 0) setSelectedSquad(data.squads[0]);
      })
      .catch(() => setError('Failed to fetch schedule'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leagueId]);

  //  Fetch my team ”
  useEffect(() => {
    if (!leagueId) return;
    fetch(`${BACKEND_URL}/player/league/${leagueId}/my-team`, { headers: authHeader })
      .then(r => r.json())
      .then(data => { setMyTeam(data.team ?? null); setMyBowlerId(data.bowlerId ?? null); })
      .catch(() => { setMyTeam(null); setMyBowlerId(null); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leagueId]);

  //  Fetch rules ”
  useEffect(() => {
    if (!leagueId) return;
    fetch(`${BACKEND_URL}/player/leagues/${leagueId}/rules`, { headers: authHeader })
      .then(r => r.json())
      .then(data => setLeagueRules(data.description || ''))
      .catch(() => setLeagueRules(''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leagueId]);

  //  Fetch SA results 
  const fetchSAResults = useCallback(async (squadId: number) => {
    if (!leagueId) return;
    if (saCache.current[squadId] !== undefined) {
      setSAResults(saCache.current[squadId]);
      return;
    }
    setLoadingSA(true);
    setSAError('');
    try {
      const res = await fetch(`${BACKEND_URL}/player/leagues/${leagueId}/squads/${squadId}/side-action-results`, { headers: authHeader });
      if (!res.ok) throw new Error();
      let results: SEResult[] = await res.json();
      if (results.some(se => se.type === 'bracket')) {
        try {
          const br = await fetch(`${BACKEND_URL}/player/leagues/${leagueId}/squads/${squadId}/side-action-brackets`, { headers: authHeader });
          if (br.ok) {
            const brData = await br.json();
            const seMap = new Map<number, any>();
            for (const se of brData.sideEvents || []) seMap.set(se.id, se);
            results = results.map(se => { const bd = seMap.get(se.id); return bd ? { ...se, brackets: bd.brackets, totalRounds: bd.totalRounds, cost: bd.cost } : se; });
          }
        } catch { /* ignore */ }
      }
      saCache.current[squadId] = results;
      setSAResults(results);
    } catch {
      setSAError('Failed to load side action results.');
      setSAResults([]);
    } finally {
      setLoadingSA(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leagueId]);

  // Reset + pre-fetch when squad changes
  useEffect(() => {
    setViewMode('league');
    setSAResults([]);
    setSAError('');
    if (selectedSquad) fetchSAResults(selectedSquad.id);
  }, [selectedSquad, fetchSAResults]);

  //  Helpers ”
  const getMatchesForSquad = (squadId: number) => {
    if (!scheduleData) return [];
    const unique = new Map<number, any>();
    scheduleData.matches.filter((m: any) => m.squadId === squadId).forEach((m: any) => { if (!unique.has(m.id)) unique.set(m.id, m); });
    return Array.from(unique.values());
  };

  const isLaneSet = (lane: number | null) => lane != null && lane > 0;

  type TabStatus = 'scored' | 'lanes' | 'pending' | 'none';
  const getTabStatus = (squad: any): TabStatus => {
    const matches = getMatchesForSquad(squad.id);
    if (matches.length === 0) return 'none';
    if (matches.some((m: any) => m.result && m.result !== '0-0')) return 'scored';
    if (matches.some((m: any) => isLaneSet(m.team1Lane) && isLaneSet(m.team2Lane))) return 'lanes';
    return 'pending';
  };

  const TAB_IDLE: Record<TabStatus, string> = {
    scored:  'border-green-500/50  bg-green-900/20  text-green-300',
    lanes:   'border-blue-500/40   bg-blue-900/20   text-blue-200',
    pending: 'border-yellow-500/40 bg-yellow-900/10 text-yellow-200',
    none:    'border-gray-600/40   bg-gray-800/20   text-gray-500',
  };
  const TAB_ACTIVE: Record<TabStatus, string> = {
    scored:  'border-green-400  bg-green-800/50  text-white shadow-lg shadow-green-900/40',
    lanes:   'border-blue-400   bg-blue-700/50   text-white shadow-lg shadow-blue-900/40',
    pending: 'border-yellow-400 bg-yellow-700/30 text-white shadow-lg',
    none:    'border-gray-500   bg-gray-700/50   text-white shadow-lg',
  };
  const DOT_COLOR: Record<TabStatus, string> = {
    scored: 'bg-green-400', lanes: 'bg-blue-400', pending: 'bg-yellow-400', none: 'bg-gray-600',
  };

  const fmtTabDate = (d: string) => new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const fmtFullDate = (d: string) => new Date(d).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  const hasSAForSquad = selectedSquad ? (saCache.current[selectedSquad.id]?.length ?? 0) > 0 : false;

  //  Loading / error / hidden states ”
  if (loading) return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 border-4 border-blue-400 border-t-transparent rounded-full animate-spin"/>
        <span className="text-blue-200 text-sm">{t('player.loadingSchedule')}</span>
      </div>
    </div>
  );
  if (error) return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800">
      <div className="text-center">
        <div className="text-red-400 text-lg mb-4">{error}</div>
        <button onClick={() => navigate(-1)} className="text-blue-300 hover:text-white underline">Go back</button>
      </div>
    </div>
  );
  if (isHidden) return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900 flex items-center justify-center px-4">
      <div className="max-w-lg w-full bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 p-10 text-center shadow-2xl">
        <div className="text-7xl mb-6">!</div>
        <h1 className="text-3xl font-bold text-white mb-2">Under Maintenance</h1>
        {hiddenLeagueName && <p className="text-blue-300 font-semibold text-lg mb-4">{hiddenLeagueName}</p>}
        <div className="w-16 h-0.5 bg-blue-400/50 mx-auto mb-6"/>
        <p className="text-blue-200 leading-relaxed mb-8">This league is currently under maintenance. Please check back soon.</p>
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-3 rounded-xl transition">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/></svg>
          Go Back
        </button>
      </div>
    </div>
  );
  if (!scheduleData) return null;

  const squads: any[] = scheduleData.squads || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800">

      {/*  Sticky Header  */}
      <div className="sticky top-0 z-30 bg-blue-950/90 backdrop-blur-md border-b border-blue-700/40 shadow-lg shadow-blue-900/20">
        <div className="max-w-7xl mx-auto px-3 py-2.5 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="flex-shrink-0 flex items-center gap-1.5 text-blue-300 hover:text-white transition-colors text-sm font-medium">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/></svg>
            <span className="hidden sm:inline">{t('player.backToDashboard')}</span>
          </button>
          <div className="w-px h-5 bg-blue-700/60 flex-shrink-0"/>
          <div className="flex-1 min-w-0">
            <h1 className="text-sm sm:text-base font-bold text-white truncate leading-tight">{scheduleData.league.name}</h1>
            <p className="text-[10px] sm:text-xs text-blue-300/80 truncate">{scheduleData.league.venue}{scheduleData.league.city ? `, ${scheduleData.league.city}` : ''}</p>
          </div>
          {/* My team compact badge (desktop) */}
          {myTeam && (
            <div className="flex-shrink-0 hidden sm:flex items-center gap-3 bg-white/10 rounded-xl px-3 py-1.5 border border-white/10">
              <div className="text-right">
                <div className="text-[10px] text-blue-300 leading-none">{t('player.myTeamLabel')}</div>
                <div className="text-xs font-semibold text-white leading-tight truncate max-w-[120px]">{myTeam.name}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] text-blue-300 leading-none">Pts</div>
                <div className="text-sm font-bold text-green-400 leading-tight">{myTeam.points ?? 0}</div>
              </div>
            </div>
          )}
          {leagueRules && (
            <button onClick={() => setShowRules(s => !s)}
              className="flex-shrink-0 flex items-center gap-1.5 bg-purple-600/40 hover:bg-purple-600/70 text-white text-xs font-medium px-3 py-1.5 rounded-xl transition border border-purple-400/30">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
              <span className="hidden sm:inline">{showRules ? t('player.hideRules') : t('player.leagueRules')}</span>
            </button>
          )}
        </div>
      </div>

      {/*  Rules Panel  */}
      {showRules && leagueRules && (
        <div className="max-w-7xl mx-auto px-3 pt-3">
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-4 border border-purple-400/30">
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-base font-bold text-white">{t('player.leagueRules')}</h2>
              <button onClick={() => setShowRules(false)} className="text-gray-300 hover:text-white transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="bg-purple-500/10 rounded-lg p-3 border border-purple-400/20 max-h-60 overflow-y-auto" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(168,85,247,0.4) rgba(255,255,255,0.1)' }}>
              <div className="text-purple-100 whitespace-pre-wrap leading-relaxed text-sm">{leagueRules}</div>
            </div>
          </div>
        </div>
      )}

      {/*  Main content ” */}
      <div className="max-w-7xl mx-auto px-3 py-4">
        <div className="flex flex-col lg:flex-row gap-5">

          {/*  Left: Squad tabs + content  */}
          <div className="flex-1 min-w-0">

            {/* Squad Tabs */}
            {squads.length > 0 ? (
              <div className="mb-4">
                <div className="overflow-x-auto pb-1 -mx-1 px-1" style={{ scrollbarWidth: 'none' }}>
                  <div className="flex gap-2 min-w-max">
                    {squads.map((squad: any) => {
                      const status = getTabStatus(squad);
                      const isActive = selectedSquad?.id === squad.id;
                      return (
                        <button key={squad.id} onClick={() => setSelectedSquad(squad)}
                          className={`flex-shrink-0 flex flex-col items-center px-4 py-2.5 rounded-xl text-xs font-medium transition-all duration-200 border-2 ${isActive ? TAB_ACTIVE[status] : TAB_IDLE[status] + ' hover:opacity-90'}`}>
                          <span className={`text-[10px] mb-0.5 ${isActive ? 'opacity-80' : 'opacity-60'}`}>{fmtTabDate(squad.date)}</span>
                          <span className="font-semibold text-sm leading-tight">{squad.name}</span>
                          {!isActive && <span className={`mt-1 w-1.5 h-1.5 rounded-full ${DOT_COLOR[status]}`}/>}
                        </button>
                      );
                    })}
                  </div>
                </div>
                {/* Legend */}
                <div className="flex items-center gap-3 mt-2 px-1 flex-wrap">
                  {([['bg-green-400', t('player.scoresSavedLabel')], ['bg-blue-400', t('player.lanesAssignedLabel')], ['bg-yellow-400', t('player.matchesOnly')], ['bg-gray-500', t('player.noMatches')]] as const).map(([color, label]) => (
                    <div key={label} className="flex items-center gap-1">
                      <div className={`w-2 h-2 rounded-full ${color}`}/>
                      <span className="text-[10px] text-blue-300/70">{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-white/5 rounded-xl p-6 text-center text-blue-300 text-sm mb-4">{t('player.noMatchesScheduled')}</div>
            )}

            {/* My team mobile strip */}
            {myTeam && (
              <div className="sm:hidden bg-white/8 rounded-xl border border-white/10 px-4 py-2.5 flex items-center justify-between mb-3">
                <div>
                  <div className="text-[10px] text-blue-300">{t('player.myTeamLabel')}</div>
                  <div className="text-sm font-bold text-white">{myTeam.name}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] text-blue-300">{t('player.leaguePointsLabel')}</div>
                  <div className="text-xl font-bold text-green-400">{myTeam.points ?? 0}</div>
                </div>
              </div>
            )}

            {/* Tab content */}
            {selectedSquad && (
              <div className="space-y-3">
                {/* Squad info bar */}
                <div className="bg-white/5 rounded-xl px-4 py-2.5 border border-white/10 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
                  <span className="text-blue-200 text-xs font-medium">{fmtFullDate(selectedSquad.date)}</span>
                  <div className="flex items-center gap-3 text-xs text-blue-300/80">
                    {selectedSquad.startTime && <span>{selectedSquad.startTime}{selectedSquad.endTime ? ` - ${selectedSquad.endTime}` : ''}</span>}
                    {selectedSquad.oil_pattern && <span>{selectedSquad.oil_pattern}</span>}
                  </div>
                </div>

                {/* View toggle */}
                <div className="flex items-center gap-3">
                  <div className="flex items-center bg-white/5 rounded-xl border border-white/10 p-1 gap-1">
                    <button onClick={() => setViewMode('league')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${viewMode === 'league' ? 'bg-blue-600 text-white shadow-md' : 'text-blue-300 hover:text-white hover:bg-white/10'}`}>
                      {t('player.leagueViewLabel') || 'League'}
                    </button>
                    <button onClick={() => { setViewMode('side_action'); fetchSAResults(selectedSquad.id); }}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all relative ${viewMode === 'side_action' ? 'bg-orange-600 text-white shadow-md' : 'text-blue-300 hover:text-white hover:bg-white/10'}`}>
                      {t('player.sideActionLabel') || 'Side Action'}
                      {hasSAForSquad && viewMode !== 'side_action' && (
                        <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-orange-400 border border-blue-900"/>
                      )}
                    </button>
                  </div>
                  {viewMode === 'side_action' && (
                    <span className="text-[10px] text-orange-300/70">
                      {loadingSA ? 'Loading...' : saResults.length > 0 ? `${saResults.length} side event${saResults.length !== 1 ? 's' : ''}` : 'No side events this week'}
                    </span>
                  )}
                </div>

                {/*  League View  */}
                {viewMode === 'league' && (
                  <div className="space-y-2">
                    {getMatchesForSquad(selectedSquad.id).length === 0 ? (
                      <div className="bg-white/5 rounded-xl p-6 text-center text-blue-300 text-sm">{t('player.noMatchesScheduled')}</div>
                    ) : (
                      <>
                        <p className="text-xs text-blue-300/50 px-1">{t('player.clickMatchesToView')}</p>
                        {getMatchesForSquad(selectedSquad.id).map((match: any) => (
                          <button key={match.id} onClick={() => setSelectedMatchId(match.id)}
                            className="w-full text-left bg-white/5 hover:bg-blue-700/30 active:bg-blue-700/50 border border-blue-400/20 hover:border-blue-400/40 rounded-xl p-4 transition-all duration-150 shadow-sm">
                            <div className="flex items-center justify-between gap-3">
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                <span className="font-semibold text-white text-sm sm:text-base truncate">{match.team1Name || `Team ${match.team1Id}`}</span>
                                <span className="flex-shrink-0 text-gray-400 text-xs font-medium bg-white/10 px-2 py-0.5 rounded-full">vs</span>
                                <span className="font-semibold text-white text-sm sm:text-base truncate">
                                  {match.team2Id === 0 ? (t('player.blind') || 'Blind') : (match.team2Name || `Team ${match.team2Id}`)}
                                </span>
                              </div>
                              <div className="flex-shrink-0 flex flex-col items-end gap-0.5">
                                {match.result && match.result !== '0-0'
                                  ? <span className="text-yellow-400 font-bold text-sm">{match.result}</span>
                                  : <span className="text-gray-500 text-xs italic">{t('player.noScoresYet')}</span>
                                }
                                <svg className="w-3.5 h-3.5 text-blue-400/40" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/></svg>
                              </div>
                            </div>
                            <div className="flex items-center gap-3 mt-2 text-xs text-blue-300/60">
                              {match.scheduledTime && <span>{match.scheduledTime}</span>}
                              {isLaneSet(match.team1Lane) && isLaneSet(match.team2Lane)
                                ? <span>{t('player.lanes')} {match.team1Lane}-{match.team2Lane}</span>
                                : <span className="italic text-gray-500">{t('player.lanesNotAssigned')}</span>
                              }
                            </div>
                          </button>
                        ))}
                      </>
                    )}
                  </div>
                )}

                {/*  Side Action View ” */}
                {viewMode === 'side_action' && (
                  <div className="space-y-3">
                    {loadingSA ? (
                      <div className="flex flex-col items-center gap-3 py-10">
                        <div className="w-8 h-8 border-4 border-orange-400 border-t-transparent rounded-full animate-spin"/>
                        <span className="text-orange-300/70 text-sm">Loading side events...</span>
                      </div>
                    ) : saError ? (
                      <div className="bg-red-900/20 border border-red-700/40 rounded-xl p-4 text-red-300 text-sm text-center">{saError}</div>
                    ) : saResults.length === 0 ? (
                      <div className="bg-white/5 rounded-xl p-8 text-center">
                        <div className="text-3xl mb-3"></div>
                        <div className="text-gray-300 font-medium mb-1">No side events this week</div>
                        <div className="text-gray-500 text-xs">Side action hasn't been set up for this squad yet.</div>
                      </div>
                    ) : (
                      saResults.map(se => <SEResultCard key={se.id} se={se} myBowlerId={myBowlerId} isEnrolled={false}/>)
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/*  Right: Sticky Sidebar  */}
          <div className="lg:w-80 xl:w-96 flex-shrink-0 flex flex-col gap-4">
            {/* My Team Card (desktop) */}
            {myTeam && (
              <div className="hidden sm:block bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 p-4 shadow-xl">
                <div className="text-xs text-blue-300/80 font-medium mb-0.5">{t('player.myTeamLabel')}</div>
                <div className="text-xl font-bold text-white mb-3 leading-tight">{myTeam.name}</div>
                <div className="flex items-center justify-between bg-white/5 rounded-xl px-3 py-2 border border-white/10">
                  <span className="text-xs text-blue-300">{t('player.leaguePointsLabel')}</span>
                  <span className="text-2xl font-bold text-green-400 leading-none">{myTeam.points ?? 0}</span>
                </div>
              </div>
            )}
            {/* Leaderboard (sticky on desktop) */}
            <div className="lg:sticky lg:top-[60px]">
              <LeaderboardCard leagueId={leagueId!}/>
            </div>
          </div>

        </div>
      </div>

      {/*  Match Modal ” */}
      {selectedMatchId && (
        <MatchScoringModal matchId={selectedMatchId} leagueId={leagueId!} onClose={() => setSelectedMatchId(null)}/>
      )}
    </div>
  );
};


export default BowlerLeagueView;
